<script setup lang="ts">
import { getCurrentInstance, onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue';
import { popupsCollection } from './helpers';
import { BaseButton } from '@qic/ui-components';

const BASE_POPUP_ZINDEX = 1000;
const POPUP_WRAPPER_CLASS = 'popup-wrapper';
const OVERFLOW_HIDDEN_CLASS = 'overflow-hidden';

const props = defineProps({
  big: { type: Boolean, default: false },
  popupV2: { type: Boolean, default: false },
  compare: { type: Boolean, default: false },
  useButton: { type: Boolean, default: true },
  isRtl: { type: Boolean, default: false },
  btnText: { type: String, default: 'Close' },
});

const emit = defineEmits(['close']);

const orderIndex = ref(-1);
const popupRef = ref<HTMLElement | null>(null);
const showEffect = ref(false);

const onClose = () => {
  showEffect.value = false;

  setTimeout(() => {
    emit('close');
  }, 200);
};

onBeforeMount(() => {
  const instance = getCurrentInstance()!;
  const { uid } = instance;
  orderIndex.value = popupsCollection.add(uid);
  document.body.classList.add(OVERFLOW_HIDDEN_CLASS);
});

onBeforeUnmount(() => {
  popupsCollection.delete(orderIndex.value);
  if (popupsCollection.size === 0) {
    document.body.classList.remove(OVERFLOW_HIDDEN_CLASS);
  }
});

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    onClose();
  }
};

const transitionName = window.innerWidth < 768 ? 'slide-up' : 'slide-left';

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
  setTimeout(() => {
    if (popupRef.value) {
      showEffect.value = true;
      popupRef.value.style.transition = '';
    }
  }, 200);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-bind="$attrs"
      class="popup-wrapper"
      :class="{'popup-v2': popupV2, active: showEffect}"
      :style="{ 'z-index': BASE_POPUP_ZINDEX + orderIndex }"
      data-test="BasePopup"
    >
      <transition>
        <div
          class="popup"
          :class="{ popup_big: props.big, rtl: isRtl, popup_compare: props.compare }"
          ref="popupRef"
        >
          <div class="popup__close" @click="onClose">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="popup__header" v-if="popupV2">
            <slot name="header"></slot>
          </div>
          <div class="popup__content">
            <div class="popup__content-inner">
              <slot></slot>
            </div>

            <slot name="button" v-bind="{ onClose }" v-if="useButton">
              <BaseButton secondary medium icon="none" @click="onClose">
                {{ btnText }}
              </BaseButton>
            </slot>
          </div>
        </div>
      </transition>
      <transition name="fade-bg">
        <div class="popup__bg" @click="onClose"></div>
      </transition>
    </div>
  </Teleport>
</template>

<style lang="scss" >
@import '@/assets/scss/mixins.scss';
@import './BasePopup.scss';

.landing-includes__popup{
  h4 {
    color: #2D2D36;
    text-align: center;
    font-size: 28px;
    font-weight: 600;
    line-height: 120%;
    margin-bottom: 8px;
  }
  h5 {
    color: #2D2D36;
    text-align: center;
    font-size: 20px;
    font-weight: 600;
    line-height: 110%;
    margin-bottom: 20px;
  }
  p {
    color: #2D2D36;
    text-align: center;
    font-size: 16px;
    line-height: 130%;
    letter-spacing: -0.1px;
    margin-bottom: 24px;
  }
  ul {
    display: grid;
    gap: 24px;
    margin-bottom: 28px;
    list-style: disc;
    padding-inline-start: 20px;

    li {
      color: #2D2D36;
      font-size: 16px;
      line-height: 130%;
      letter-spacing: -0.1px;
    }
  }
  &__btn{
    &.btn{
      margin: 0;
      border: 1px solid #DEDEED;
      color: #2D2D36;
      min-height: 48px;
    }
  }
  &__nav{
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    .item{
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border-radius: 16px;
      background-color: #F5F5F9;
      cursor: pointer;
      transition: all .2s linear;
      &-next{
        svg{
          transform: scaleX(-1);
        }
      }
      &:hover{
        background-color: #EFEAFE;
      }
      @include rtl{
        svg{
          transform: scaleX(-1);
        }
        &-next{
          svg{
            transform: scaleX(1);
          }
        }
      }
    }
    @media(max-width: 720px){
      &:last-of-type{
        order: 2;
      }
    }
  }
  &__footer{
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 16px;
    padding: 16px 16px 0;
    margin-top: auto;
    border-top: 1px solid #DEDEED;
  }
}
</style>
